<template>
  <div @click="handleClick">
    <AdSlider
      :style="sizeStyle"
      ref="wrapper"
      :class="[classes.adSlider, 'background']"
    >
      <template v-slot:preroll>
        <Preroll :sizeId="sizeStyle._id" />
      </template>
      <template v-slot:adcontent>
        <AdContent :sizeId="sizeStyle._id" />
      </template>
    </AdSlider>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watchEffect } from "vue";
import {
  useDimensionSync,
  syncLayoutDependencies
} from "@fresh8/vue-utils/src/dimensionSync";
import { createUseStyles } from "@fresh8/vue-jss/src";

import {
  injectBrand,
  injectAdTag,
  BrandContext,
  AdTagContext
} from "@fresh8/ad-data-composition/src/brand";
import {
  injectBetData,
  BetDataContext
} from "@fresh8/ad-data-composition/src/betData";
import { injectRuntimeConfig } from "@fresh8/ad-data-composition/src/runtimeConfig";
import { provideLayout, useLayout } from "../hooks/useLayout";
import { provideAdSize } from "../hooks/useAdSize";
import {
  provideTemplateSize,
  useTemplateSize
} from "@fresh8/ad-data-composition/src/useTemplateSize";

import AdContent from "./AdContent.vue";
import AdSlider from "./AdSlider.vue";
import Preroll from "./PreRoll.vue";
import { useAnalytics } from "../hooks/useAnalytics";

const useStyles = createUseStyles({
  adSlider: {
    backgroundColor: props => props.backgroundColor || ""
  }
});

export default defineComponent({
  components: {
    AdContent,
    AdSlider,
    Preroll
  },
  setup() {
    const wrapper = ref(null);
    const rc = injectRuntimeConfig();
    const {
      adTemplateBranding,
      enableBackgroundClick
    } = injectBrand() as BrandContext;
    const adTag = injectAdTag() as AdTagContext;
    const { links } = injectBetData() as BetDataContext;
    const { handleClick: backgroundClick } = useAnalytics({}, "Ad Background");

    const handleClick = () => {
      if (enableBackgroundClick.value && rc.value.enableLinks) {
        backgroundClick();
        window.open(links.value.registration);
      }
    };

    const { dimensions } = useDimensionSync(wrapper);
    const layoutContext = useLayout(dimensions);
    const templateSizeContext = useTemplateSize(adTag);

    provideLayout(layoutContext);
    provideTemplateSize(templateSizeContext);
    provideAdSize(dimensions);

    watchEffect(onInvalidate => {
      let updateParentToken;
      function updateParent() {
        window.parent.postMessage(
          { type: "f8_size", contentHeight: dimensions.value.height },
          "*"
        );

        updateParentToken = setTimeout(updateParent, 100);
      }

      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      updateParent();
      onInvalidate(() => updateParentToken && clearTimeout(updateParentToken));
    });

    const size = computed(() => {
      rc.value.globalEmit?.("adResize", {
        height: dimensions.value.height,
        width: dimensions.value.width
      });

      const height =
        adTag.adTagSize.value?.height && `${adTag.adTagSize.value?.height}px`;

      if (adTag.responsive.value) {
        return {
          width: "100%",
          height: height,
          minHeight: !height && "270px",
          _id: adTag.adTagSize.value._id
        };
      }

      return {
        width: `${adTag.adTagSize.value?.width}px`,
        height: height,
        _id: adTag.adTagSize.value._id
      };
    });

    syncLayoutDependencies([
      computed(() => size.value?.width),
      computed(() => size.value?.height),
      adTag.responsive
    ]);

    const reativeStyles = computed(() => ({
      ...size.value,
      ...adTemplateBranding.value
    }));

    const classesRef = useStyles(reativeStyles);

    return {
      classes: classesRef,
      wrapper,
      sizeStyle: size,
      handleClick
    };
  }
});
</script>
